/* eslint-disable no-nested-ternary */
/* eslint-disable no-underscore-dangle */
/* eslint-disable max-len */
/* eslint-disable react/prop-types */
// CalendarCreateContent — Create New Calendar / Create New Program page.
//
// Visual values pulled directly from Figma "AI Import" file via the Figma MCP:
//   - Calendar variant: node 7220:7849
//   - Program variant:  node 7220:8712
// Design tokens mapped from theme/* + typography/* in that file.
import React, {
  useState, useEffect, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Select from 'react-select';
import IcomoonReact from 'icomoon-react';
import { VariableSizeList as List } from 'react-window';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import iconSet from '../../../../shared/images/teambuildr-selection.json';
import Text from '../../../../shared/components/Text/Text';
import { showErrorToast } from '../../../../shared/utils/toastManager';
import {
  createParentCalendar,
  createParentChildCalendar,
  createSavedWorkout,
  setCreateModalProgramType,
  setProgramType,
  fetchSavedWorkouts,
  createProgramTag,
} from '../../ducks/calendarActions';
import SelectBox from '../../../../shared/_TBComponents/SelectBox';

// Page shell. Centers a 600px content column (Figma frame: 600px wide
// container inset 32px from the top of the page body). isModal trims the
// generous full-page padding when the form is embedded in a modal.
const Container = styled('div')`
  width: 100%;
  margin: 0 auto;
  padding: ${(props) => (props.isModal ? '20px' : '64px 20px')};
  background: #ffffff;
  min-height: ${(props) => (props.isModal ? 'auto' : '100%')};
  overflow: ${(props) => (!props.isModal && 'auto')};
  position: relative;

  @media (max-width: 768px) {
    padding: ${(props) => (props.isModal ? '16px' : '40px 16px')};
  }
`;

// Content column — Figma node 7220:7849: flex column, 32px gap between
// each section (breadcrumb / title / AI card / divider / fields / actions),
// centered, max 600px wide.
const ContentWrapper = styled('div')`
  max-width: 600px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
`;

// Breadcrumb bar — node 7220:7850: 8px gap, 13px text. First crumb Bold
// #444 (clickable back action), separator slash #bdbdbd, current crumb
// Regular #bdbdbd.
const Breadcrumbs = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  line-height: 1.5;
`;

const BreadcrumbButton = styled('button')`
  background: none;
  border: none;
  padding: 0;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #444444;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};

  &:hover {
    text-decoration: ${(props) => (props.disabled ? 'none' : 'underline')};
  }
`;

const BreadcrumbSeparator = styled('span')`
  color: #bdbdbd;
  font-size: 13px;
`;

const BreadcrumbCurrent = styled('span')`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: #bdbdbd;
`;

// Title — node 7220:7851: Nunito Sans Bold (700), 34px, line-height 1.3,
// letter-spacing -0.34px (=-1% of 34px), centered, #444.
const Title = styled('h1')`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 34px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.34px;
  color: #444444;
  text-align: center;
  margin: 0;
  width: 100%;

  @media (max-width: 480px) {
    font-size: 28px;
  }
`;

// AI Import promo card — node 7220:7852: white surface, 3px solid #efc04b
// border, 8px radius, 24px padding, warm orange shadow. Inner content is a
// 32px-gap column whose CTA hugs the right edge (items-end).
const AICard = styled('div')`
  width: 100%;
  background: #ffffff;
  border: 3px solid #efc04b;
  border-radius: 8px;
  padding: 24px;
  box-shadow:
    0 16px 12px rgba(248, 153, 58, 0.2),
    0 0 0.5px rgba(248, 153, 58, 0.1);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const AICardBody = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const AICardHeader = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

// 48px gradient square — node 7220:7856: rounded 8px, 8px padding wrapping a
// 32px flash icon rendered white. Gradient angle/stops verbatim from Figma.
const AIIconBadge = styled('div')`
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-image: linear-gradient(
    -79.44deg,
    rgb(239, 192, 75) 0.9%,
    rgb(248, 153, 58) 26.8%,
    rgb(255, 119, 43) 99.9%
  );
`;

// Heading — node 7220:7858: Nunito Sans Bold (700), 24px, #444.
const AIHeading = styled('p')`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.5;
  color: #444444;
  margin: 0;
`;

// Description — node 7220:7859: Nunito Sans Regular (400), 16px, #9E9E9E,
// max-width 527px in the mock.
const AIDescription = styled('p')`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #9e9e9e;
  margin: 0;
  max-width: 527px;
`;

const AIPills = styled('div')`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 8px;
`;

// Pill — node 7220:7861: bg rgba(189,189,189,0.3), 16px radius, 8px/4px
// padding, 4px gap between icon and label. Label Nunito Sans SemiBold (600)
// 12px #444.
const AIPill = styled('div')`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 16px;
  background: rgba(189, 189, 189, 0.3);
`;

const AIPillText = styled('span')`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  color: #444444;
  white-space: nowrap;
`;

// CTA — node 7220:7870: bg #444, 4px radius, 16px/6px padding, 8px gap,
// label Nunito Sans Bold (700) 13px white with 0.39px tracking + arrow.
const AIButton = styled('button')`
  align-self: flex-end;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #444444;
  border: none;
  border-radius: 4px;
  padding: 6px 16px;
  cursor: pointer;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.39px;
  line-height: 1.5;
  color: #ffffff;
  transition: background 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    background: #2a2a2a;
    box-shadow: 0 4px 16px rgba(48, 49, 51, 0.25);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

// "or" divider — node 7220:7871: two 1px rules flanking a Regular 16px
// #9E9E9E "or", 16px gap.
const Divider = styled('div')`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const DividerLine = styled('div')`
  flex: 1 0 0;
  min-width: 0;
  height: 0;
  border-top: 1px solid #e0e0e0;
`;

const DividerLabel = styled('span')`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #9e9e9e;
`;

// Fields column — node 7220:7875: 16px gap between fields.
const Fields = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

// Text Field — node 7220:7876: label + input stacked with 2px gap.
const Field = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
`;

// Label — Nunito Sans Bold (700), 16px, #444.
const Label = styled('label')`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
  color: #444444;
`;

// Input — node I7220:7876;495:18138: white bg, 2px solid #E0E0E0 border,
// 4px radius, 16px/8px padding. Text #444, placeholder #9E9E9E, 16px.
const Input = styled('input')`
  width: 100%;
  box-sizing: border-box;
  padding: 8px 16px;
  border: 2px solid ${(props) => (props.hasError ? '#E74C3C' : '#e0e0e0')};
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #444444;
  background: #ffffff;

  &::placeholder {
    color: #9e9e9e;
  }

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? '#E74C3C' : '#9e9e9e')};
  }
`;

// Inline validation message — Nunito Sans 13px, calendar-module error red.
const ErrorText = styled('span')`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.5;
  color: #e74c3c;
`;

// react-select restyled to match the input above — 2px #E0E0E0 border,
// 4px radius, 16px/8px padding, #9E9E9E placeholder, #444 value text.
const SelectContainer = styled('div')`
  width: 100%;

  .react-select__control {
    border: 2px solid #e0e0e0;
    border-radius: 4px;
    min-height: 42px;
    box-shadow: none;
    background: #ffffff;
    font-family: 'Nunito Sans', sans-serif;

    &:hover {
      border-color: #e0e0e0;
    }
  }

  .react-select__control--is-focused {
    border-color: #9e9e9e;
    box-shadow: none;
  }

  .react-select__value-container {
    padding: 8px 16px;
  }

  .react-select__single-value {
    color: #444444;
    font-family: 'Nunito Sans', sans-serif;
    font-size: 16px;
  }

  .react-select__placeholder {
    color: #9e9e9e;
    font-family: 'Nunito Sans', sans-serif;
    font-size: 16px;
  }

  .react-select__menu {
    border: none;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  }

  .react-select__option {
    font-family: 'Nunito Sans', sans-serif;
    font-size: 16px;
    color: #444444;
    cursor: pointer;
    padding: 8px 16px;
  }

  .react-select__option--is-selected,
  .react-select__option--is-focused {
    background-color: #f5f5f5;
    color: #444444;
  }

  .react-select__indicator-separator {
    display: none;
  }

  .react-select__dropdown-indicator {
    color: #9e9e9e;
  }
`;

const TagColorDot = styled('span')`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid #E0E0E0;
  background: ${(props) => props.color || '#FFFFFF'};
  flex-shrink: 0;
`;

// Action row — node 7220:7878: two equal-width buttons, 8px gap, 32px tall.
const ButtonContainer = styled('div')`
  display: flex;
  gap: 8px;
  width: 100%;
`;

// Shared button base — Nunito Sans Bold (700) 13px, 0.39px tracking, 4px
// radius, 32px tall (16px/6px padding), uppercase label.
const ActionButton = styled('button')`
  flex: 1 0 0;
  min-width: 0;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 16px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.39px;
  line-height: 1.5;
  cursor: pointer;
  transition: background 0.18s ease, border-color 0.18s ease;

  ${(props) => (props.primary ? `
    border: none;
    background: ${props.disabled ? '#e0e0e0' : '#444444'};
    color: ${props.disabled ? '#757575' : '#ffffff'};
    cursor: ${props.disabled ? 'not-allowed' : 'pointer'};

    &:hover {
      background: ${props.disabled ? '#e0e0e0' : '#2a2a2a'};
    }
  ` : `
    border: 2px solid #eeeeee;
    background: #ffffff;
    color: #444444;

    &:hover {
      background: #fafafb;
      border-color: #c9c9d4;
    }
  `)}
`;

// Max length for the calendar/program name (including spaces).
const MAX_NAME_LENGTH = 80;

const MenuList = (props) => {
  const { children, maxHeight } = props;
  const listRef = useRef({});
  const rowHeights = useRef({});
  /** Calculate height based on the children length or a default */
  const childrenLengthBy40 = children?.length ? children.length * 40 : 40;
  const height = Math.min(childrenLengthBy40, maxHeight);
  const getRowHeight = (index) => rowHeights.current[index] + 8 || 40;

  return (
    <div style={{ height }}>
      {!children.length ? (
        <List
          ref={listRef}
          height={40}
          itemCount={1}
          itemSize={() => 40}
        >
          {({ style }) => (
            <div style={{
              ...style, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            >
              <Text fontSize='16px' color='hsl(0, 0%, 60%)'>type to create new tag</Text>
            </div>
          )}
        </List>

      ) : (
        <List
          ref={listRef}
          height={height}
          itemCount={children.length}
          itemSize={(index) => getRowHeight(index)}
        >
          {({ index, style }) => {
            const rowRef = useRef({});

            useEffect(() => {
              if (rowRef.current) {
                const rowHeight = rowRef.current.getBoundingClientRect().height;
                /** Store this index's height and reset the List after each update */
                if (rowHeights.current[index] !== rowHeight) {
                  rowHeights.current[index] = rowHeight;
                  listRef.current?.resetAfterIndex(index); /** Reset from this index to recalculate sizes */
                }
              }
            }, [index]);

            return (
              <div style={style}>
                <div ref={rowRef}>
                  {children[index]}
                </div>
              </div>
            );
          }}
        </List>
      )}
    </div>
  );
};

const MemoizedMenuList = React.memo(MenuList);

const CalendarCreateContent = ({
  isModal = false, onBack, closeModal, flags,
}) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isCreatingCalendar, setIsCreatingCalendar] = useState(false);
  const [formData, setFormData] = useState({
    calendarType: 'calendar', // Default to calendar type
    calendarName: '',
    parentCalendar: '',
    parentCalendarName: '',
    tags: [],
  });
  const parentCalendars = useSelector((state) => state.calendar.data.parentCalendars || []);
  const newlyCreatedProgram = useSelector((state) => state.calendar.data.newlyCreatedProgram);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const accountCode = currentUser?.accountCode;
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxNonDateBasedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );
  const isProgramTagsLoading = useSelector((state) => state.calendar.ui.isProgramTagsLoading);
  const calendarCreationError = useSelector((state) => state.calendar.data.calendarCreationError);
  const createModalProgramType = useSelector((state) => state.calendar.data.createModalProgramType);
  const tagsForAllPrograms = useSelector((state) => state.calendar.data.tagsForAllPrograms);
  const [inputValue, setInputValue] = useState(null);
  const [isNewTagOptionLoading, setIsNewTagOptionLoading] = useState(false);

  /** make sure tags always get put in alphabetically order
   * becuase we can make new tags in the dropdown */
  const memoizedTagOptions = useMemo(
    () => [...tagsForAllPrograms]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((tag) => ({
        ...tag,
        avatar: tag.color && tag.color !== '#FFFFFF'
          ? <TagColorDot color={tag.color} />
          : undefined,
        avatarRight: true,
      })),
    [tagsForAllPrograms],
  );

  const isProgram = createModalProgramType === 'free-form-program';

  // Sync program type from URL on mount for deep-linking support
  useEffect(() => {
    if (location.pathname.includes('/program/create')) {
      dispatch(setCreateModalProgramType('free-form-program'));
    } else if (location.pathname.includes('/calendar/create')) {
      dispatch(setCreateModalProgramType('calendar'));
    }
  }, [location.pathname]);

  useEffect(() => {
    if (newlyCreatedProgram) {
      dispatch(fetchSavedWorkouts(accountCode));
    }
  }, [newlyCreatedProgram]);

  const selectedCalendar = (createModalProgramType === 'calendar' && selectedReduxCalendar && Object.keys(selectedReduxCalendar).length > 0)
    ? selectedReduxCalendar
    : (selectedReduxNonDateBasedProgram
      && Object.keys(selectedReduxNonDateBasedProgram).length > 0)
      ? selectedReduxNonDateBasedProgram
      : null;

  useEffect(() => {
    if (isCreatingCalendar && selectedCalendar) {
    // Calendar was successfully created and selected
      if (isModal && closeModal) {
        closeModal(); // Close modal if in modal context
      } else {
        history.push('/build'); // Navigate if not in modal
      }
    }
  }, [
    selectedReduxCalendar,
    selectedReduxNonDateBasedProgram, // add this dependency
    isCreatingCalendar,
    history,
    isModal,
    closeModal,
  ]);

  // Watch for calendar creation errors and display toast
  useEffect(() => {
    if (isCreatingCalendar && calendarCreationError) {
      // Extract error message from the error object
      let errorMessage = 'Failed to create calendar. Please try again.';

      if (calendarCreationError) {
        errorMessage = calendarCreationError;
      }

      showErrorToast(errorMessage);

      // Reset the creating flag so user can try again
      setIsCreatingCalendar(false);
    }
  }, [calendarCreationError, isCreatingCalendar]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCancel = () => {
    if (isModal && onBack) {
      onBack(); // Go back to previous slide in modal
      if (isProgram) {
        dispatch(setCreateModalProgramType('calendar'));
      }
    } else {
      history.push('/build'); // Navigate away if not in modal
    }
  };

  const handleImportWithAI = () => {
    history.push('/import/program');
  };

  const handleSave = () => {
    if (!currentUser?.accountCode) {
      return;
    }

    if (formData.calendarName.length > MAX_NAME_LENGTH) {
      return;
    }

    // Set flag to indicate calendar creation is in progress
    setIsCreatingCalendar(true);

    if (createModalProgramType === 'calendar') {
      dispatch(setProgramType('calendar'));
      const requestBody = {
        name: formData.calendarName,
        workoutVisibility: 0, // Default to "All" (value 0)
      };

      // Check if parent calendar is selected
      const hasParent = formData.parentCalendar
      && formData.parentCalendar !== '';

      if (hasParent) {
      // Call createParentChildCalendar
        requestBody.parentId = parseInt(formData.parentCalendar, 10);
        requestBody.parentName = formData.parentCalendarName;
        dispatch(createParentChildCalendar({
          accountCode: currentUser.accountCode,
          ...requestBody,
        }));
      } else {
      // Call createParentCalendar
        dispatch(createParentCalendar({
          accountCode: currentUser.accountCode,
          ...requestBody,
        }));
      }
    }
    if (isProgram) {
      dispatch(setProgramType('free-form-program'));
      const requestBody = {
        programName: formData.calendarName,
        numberOfDays: 1,
        saveInfoTagIds: formData.tags,
      };
      dispatch(createSavedWorkout(accountCode, {
        ...requestBody,
      }));
    }
  };

  const isNameOverLimit = formData.calendarName.length > MAX_NAME_LENGTH;

  const isFormValid = () => formData.calendarName.trim() !== ''
    && formData.calendarName.length <= MAX_NAME_LENGTH;

  const getCalendarOptions = () => {
    const options = [
      { value: '', label: 'Select a calendar' },
    ];

    if (parentCalendars && parentCalendars.length > 0) {
      parentCalendars.forEach((calendar) => {
        options.push({
          value: calendar.id,
          label: calendar.name,
        });
      });
    }

    return options;
  };

  const getSelectedCalendarOption = () => {
    const options = getCalendarOptions();
    return options.find((option) => option.value === formData.parentCalendar) || null;
  };

  const pageTitle = isProgram ? 'Create New Program' : 'Create New Calendar';
  const currentCrumb = isProgram ? 'Program Details' : 'Calendar Details';
  const nameLabel = isProgram ? 'Program Name' : 'Calendar Name';
  const namePlaceholder = isProgram ? 'Program name' : 'Calendar name';

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      fontFamily: 'Nunito Sans',
      fontSize: '16px',
      fontWeight: 400,
      background: 'white',
      borderColor: state.isFocused ? 'lightgray' : 'lightgray',
      boxShadow: state.isFocused ? 'none' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? 'lightgray' : 'lightgray',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      padding: '4px',
      borderRadius: '4px',
      backgroundColor: 'white',
      color: state.isSelected ? '#BDBDBD' : '#444444',
      fontFamily: 'Nunito Sans',
      fontSize: '16px',
      marginBottom: '8px',
      fontWeight: state.data.__isNew__ ? 700 : 400,
      '&:hover': {
        backgroundColor: '#FCFCFC',
      },
    }),
    multiValue: (styles) => ({
      ...styles,
      backgroundColor: '#FCFCFC',
      border: '1px solid #EEEEEE',
      borderRadius: '4px',
    }),
    multiValueLabel: (styles) => ({
      ...styles,
      color: '#444444',
    }),
    multiValueRemove: (styles) => ({
      ...styles,
      color: '#9E9E9E',
    }),
    menu: (provided) => ({
      ...provided,
      border: '0px',
      minWidth: '250px',
      padding: '16px',
      marginTop: '0px',
      marginBottom: '0px',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9E9E9E',
    }),
    input: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? 'inherit' : provided.color, /** Use 'inherit' to maintain original color */
    }),
  };

  return (
    <Container isModal={isModal}>
      <ContentWrapper>
        <Breadcrumbs>
          <BreadcrumbButton
            onClick={isModal ? (onBack || undefined) : () => history.push('/build')}
            disabled={isModal && !onBack}
          >
            Program Type
          </BreadcrumbButton>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
          <BreadcrumbCurrent>{currentCrumb}</BreadcrumbCurrent>
        </Breadcrumbs>

        <Title>{pageTitle}</Title>

        {flags && flags.aiProgramImport && (
          <>
            <AICard>
              <AICardBody>
                <AICardHeader>
                  <AIIconBadge>
                    <IcomoonReact
                      iconSet={iconSet}
                      size={28}
                      icon='lightning-bolt'
                      color='#ffffff'
                    />
                  </AIIconBadge>
                  <AIHeading>Import a spreadsheet with AI</AIHeading>
                </AICardHeader>
                <AIDescription>
                  Upload your spreadsheet and let us do the heavy lifting.
                  We&apos;ll detect workouts, match exercises, and build a program you can assign.
                </AIDescription>
                <AIPills>
                  <AIPill>
                    <IcomoonReact iconSet={iconSet} size={12} icon='file' color='#444444' />
                    <AIPillText>Excel &amp; CSV</AIPillText>
                  </AIPill>
                  <AIPill>
                    <IcomoonReact iconSet={iconSet} size={12} icon='lift' color='#444444' />
                    <AIPillText>Exercise matching</AIPillText>
                  </AIPill>
                  <AIPill>
                    <IcomoonReact iconSet={iconSet} size={14} icon='clock' color='#444444' />
                    <AIPillText>Fastest setup</AIPillText>
                  </AIPill>
                </AIPills>
              </AICardBody>
              <AIButton type='button' onClick={handleImportWithAI}>
                Import with AI
                <IcomoonReact iconSet={iconSet} size={14} icon='right-arrow' color='#ffffff' />
              </AIButton>
            </AICard>

            <Divider>
              <DividerLine />
              <DividerLabel>or</DividerLabel>
              <DividerLine />
            </Divider>
          </>
        )}

        <Fields>
          <Field>
            <Label htmlFor='calendar-name-input'>{nameLabel}</Label>
            <Input
              id='calendar-name-input'
              type='text'
              value={formData.calendarName}
              onChange={(e) => handleInputChange('calendarName', e.target.value)}
              placeholder={namePlaceholder}
              hasError={isNameOverLimit}
            />
            {isNameOverLimit && (
              <ErrorText>
                {`${isProgram ? 'Program' : 'Calendar'} name cannot exceed ${MAX_NAME_LENGTH} characters`}
              </ErrorText>
            )}
          </Field>

          {isProgram && (
            <SelectBox
              isDisabled={isNewTagOptionLoading || isProgramTagsLoading}
              options={memoizedTagOptions}
              value={formData.tags?.map((tagId) => memoizedTagOptions.find(
                (option) => option.id === tagId,
              )) || []}
              onInputChange={(value) => setInputValue(value)}
              onChangeFunction={(selectedOptions) => {
                /** Extract tag IDs from the selected options */
                const selectedTagIds = (selectedOptions || []).map((option) => option.id);
                /* Update the 'tags' field in the form */
                handleInputChange('tags', selectedTagIds);
              }}
              title='Tags (optional)'
              titleStyledProps={{ fontSize: '16px', fontWeight: 700 }}
              customStyles={customSelectStyles}
              isMulti
              isCreateable
              isLoading={isNewTagOptionLoading || isProgramTagsLoading}
              onCreateOption={(option) => {
                setIsNewTagOptionLoading(true);
                dispatch(createProgramTag(accountCode, option, {
                  onSuccess: (response) => {
                    const newOptionReturnedFromApi = { name: response.name, id: response.id };
                    setFormData((prev) => ({
                      ...prev,
                      tags: [...(prev.tags || []), newOptionReturnedFromApi.id],
                    }));
                    setIsNewTagOptionLoading(false);
                  },
                  onError: () => {
                    showErrorToast('Opps! Something went wrong. Unable to create new tag.');
                    setIsNewTagOptionLoading(false);
                  },
                }));
              }}
              noOptionsMessage={!inputValue ? 'Type to add new tag' : 'type to create new tag'}
              menuListComponent={MemoizedMenuList}
            />
          )}

          {!isProgram && (
            <Field>
              <Label>Select Parent Calendar (Optional)</Label>
              <SelectContainer>
                <Select
                  value={getSelectedCalendarOption()}
                  onChange={(selectedOption) => {
                    if (selectedOption) {
                      handleInputChange('parentCalendar', selectedOption.value);
                      handleInputChange('parentCalendarName', selectedOption.label);
                    } else {
                      // Clear both when selection is removed
                      handleInputChange('parentCalendar', '');
                      handleInputChange('parentCalendarName', '');
                    }
                  }}
                  options={getCalendarOptions()}
                  className='react-select'
                  classNamePrefix='react-select'
                  placeholder='Select a calendar'
                  isSearchable
                  isClearable
                />
              </SelectContainer>
            </Field>
          )}
        </Fields>

        <ButtonContainer>
          <ActionButton type='button' onClick={handleCancel}>
            BACK
          </ActionButton>
          <ActionButton
            type='button'
            primary
            onClick={handleSave}
            disabled={!isFormValid()}
          >
            CREATE
          </ActionButton>
        </ButtonContainer>
      </ContentWrapper>
    </Container>
  );
};

CalendarCreateContent.propTypes = {
  isModal: PropTypes.bool,
  onBack: PropTypes.func,
  closeModal: PropTypes.func,
  flags: PropTypes.shape({
    aiProgramImport: PropTypes.bool,
  }),
};

CalendarCreateContent.defaultProps = {
  isModal: false,
  onBack: null,
  closeModal: null,
  flags: {},
};

export default withLDConsumer()(CalendarCreateContent);
